import { useLookbookDetailsModalContext } from '@app/components/lookbook/context/LookbookDetailsModalContext';
import { ProductAttributes } from '@app/components/products/ProductAttributes';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getDefaultImageAspectRatio } from '@app/ducks/helpers';
import { getVariantsForMatrix } from '@app/logic/products';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { useIsScreenSize } from '@app/utils/hooks';
import { usePdmArrowControls } from '@app/utils/hooks/usePdmArrowControls';
import React from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { LookbookRelatedProducts } from '../../RelatedProducts/LookbookRelatedProducts';
import { useProductDetailsLayoutContext } from '../context/ProductDetailsLayoutContext';
import { useProductDetailsVariantContext } from '../context/ProductDetailsVariantContext';
import { LookbookProductDetailsButton } from '../ProductDetailsButton/LookbookProductDetailsButton';
import { ProductDetailsDescription } from '../ProductDetailsDescription';
import { ProductDetailsGallery } from '../ProductDetailsGallery';
import { LookbookVariantsThumbnailsNavigation } from '../VariantsThumbnailsNavigation/LookbookVariantsThumbnailsNavigation';

import { LookbookProductDetailsMatrices } from './LookbookProductDetailsMatrices';
import { LookbookProductDetailsModalHeader } from './LookbookProductDetailsModalHeader';

const TRANSITION_TIMEOUT = 150;

interface Props {
  onHideModal: () => void;
  hideArrows: boolean;
}

export const LookbookProductDetailsModalContent = ({ onHideModal, hideArrows }: Props) => {
  const { activeMediaIndex, activeVariant, medias, productDetails, variants, handleMediaIndexChange } = useProductDetailsVariantContext();
  const { isDesktop } = useProductDetailsLayoutContext();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const aspectRatio = useSelector(getDefaultImageAspectRatio);
  const isMobile = useIsScreenSize(BREAKPOINTS.LG);
  const isLookbookWishlistEnabled = getIsFeatureEnabled('ENABLE_LOOKBOOK_WISHLIST');
  const { previousProductUrl, nextProductUrl, isReadOnly, lookbookId } = useLookbookDetailsModalContext();
  const navigationRef = React.useRef<HTMLDivElement>(null);

  usePdmArrowControls({
    activeMediaIndex,
    mediasLength: medias.length,
    nextProductUrl,
    onMediaIndexChange: handleMediaIndexChange,
    previousProductUrl,
    ref: navigationRef,
    scrollRef,
  });

  const filteredVariants = React.useMemo(() => getVariantsForMatrix(variants, productDetails.product), [productDetails.product, variants]);

  React.useEffect(() => {
    setTimeout(() => {
      scrollRef.current?.scrollTo(0, 0);
    }, TRANSITION_TIMEOUT);
  }, [productDetails.product]);

  return (
    <div
      ref={isDesktop ? navigationRef : scrollRef}
      id={isDesktop ? undefined : 'productDetailsModal'}
      className="mx-auto h-full w-full max-w-screen-xxl overflow-y-auto bg-white focus:outline-none focus-visible:outline-none lg:flex lg:overflow-hidden lg:bg-transparent"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      <div
        className="sticky top-0 max-h-[calc(100%-5.5rem)] shrink-0 sm:max-h-[calc(100%-10.5rem)] lg:relative lg:max-h-full lg:max-w-[50%]"
        style={{ aspectRatio: isMobile ? undefined : `1 / ${aspectRatio}` }}
      >
        <SwitchTransition>
          <CSSTransition
            key={productDetails.product}
            timeout={TRANSITION_TIMEOUT}
            classNames={{
              enter: 'opacity-0',
              enterActive: '!opacity-100 transition-opacity',
              exit: 'opacity-100',
              exitActive: '!opacity-0 transition-opacity',
            }}
          >
            <ProductDetailsGallery activeMediaIndex={activeMediaIndex} productDetails={productDetails} medias={medias} />
          </CSSTransition>
        </SwitchTransition>
      </div>
      <div
        ref={isDesktop ? scrollRef : undefined}
        id={isDesktop ? 'productDetailsModal' : undefined}
        className="contents w-full bg-white lg:block lg:overflow-y-auto"
      >
        <LookbookProductDetailsModalHeader
          productDetails={productDetails}
          activeVariant={activeVariant}
          onHideModal={onHideModal}
          hideArrows={hideArrows}
        />
        <div className="relative z-elevated bg-white px-4 pb-5 pt-2 sm:gap-8 sm:pb-24 sm:pt-6 lg:px-8">
          <SwitchTransition>
            <CSSTransition
              key={productDetails.product}
              timeout={TRANSITION_TIMEOUT}
              classNames={{
                enter: 'opacity-0',
                enterActive: '!opacity-100 transition-opacity',
                exit: 'opacity-100',
                exitActive: '!opacity-0 transition-opacity',
              }}
            >
              <div className="flex flex-col gap-4 pb-1 sm:gap-8">
                <ProductAttributes product={activeVariant} shouldShowRrp />
                <LookbookVariantsThumbnailsNavigation
                  productDetails={productDetails}
                  variants={variants}
                  activeVariant={activeVariant}
                  isReadOnly={isReadOnly}
                />
                {isLookbookWishlistEnabled && !isReadOnly && (
                  <div className="w-64">
                    <LookbookProductDetailsButton variants={variants} productDetails={productDetails} activeVariant={activeVariant} />
                  </div>
                )}
                <ProductDetailsDescription productDetails={productDetails} activeVariant={activeVariant} />
                <LookbookProductDetailsMatrices
                  variants={filteredVariants}
                  tableMappings={productDetails.tableMappings}
                  defaultLocalizedChart={productDetails.defaultLocalizedChart}
                />
                <LookbookRelatedProducts isReadOnly={isReadOnly} lookbookId={lookbookId} />
              </div>
            </CSSTransition>
          </SwitchTransition>
        </div>
      </div>
    </div>
  );
};
